import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Journalist Management (Approval Request) page.
 * Targets search bar and table. Table step description mentions the sidebar menu.
 */
export const usePledgeJournalistManagementTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnJournalistManagement =
    pathname === ROUTE_NAMES.JOURNALIST_MANAGEMENT;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeJournalistManagement",
      enabled: isOnJournalistManagement,
      steps: [
        {
          id: "pledge-journalist-mgmt-search",
          targetSelector: '[data-tour-item="pledge-journalist-mgmt-search"]',
          title: t("tourGuide.pledgeJournalistManagement.search.title"),
          description: t(
            "tourGuide.pledgeJournalistManagement.search.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-journalist-mgmt-table",
          targetSelector: '[data-tour-item="pledge-journalist-mgmt-table"]',
          title: t("tourGuide.pledgeJournalistManagement.table.title"),
          description: t(
            "tourGuide.pledgeJournalistManagement.table.description",
          ),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnJournalistManagement, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.PledgeJournalistManagement, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeJournalistManagementTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeJournalistManagement,
    isOnTourPath: isOnJournalistManagement && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeJournalistManagementTourGuide.isActive) {
      setActiveTourModule("pledgeJournalistManagement");
      setCurrentTourStep(pledgeJournalistManagementTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeJournalistManagementTourGuide.isActive,
    pledgeJournalistManagementTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeJournalistManagementTourGuide,
    isMobile,
  };
};
